import {
  App,
  AppListItem,
  AppCategory as SharedAppCategory,
  RemixAppDto,
  RemixAppResponse,
  ValidateAppConnectionsDto,
  ValidateAppConnectionsResponse,
  ConnectionRequirement,
  EnvVariable,
} from '@optidev/shared-types';

import { apiClient, createAuthHeaders } from '../api-client';

export interface AppFilters {
  category?: string;
  tags?: string;
  search?: string;
}

export interface AppCategoryCount {
  category: string;
  count: number;
}

export interface AppMetadata {
  version?: string;
  author?: string;
  framework?: string;
}

export interface SubmitAppDto {
  name: string;
  categories: SharedAppCategory[];  // Array of categories (min 1)
  description?: string;
  tags?: string[];
  thumbnailUrl?: string;
  customThumbnailUrl?: string | null;  // null clears the saved value
  previewUrl?: string;
  connections?: ConnectionRequirement[];
  envVariables?: EnvVariable[];
  metadata?: AppMetadata;
}

export interface ResubmitAppDto {
  name?: string;
  categories?: SharedAppCategory[];  // Array of categories
  description?: string;
  tags?: string[];
  thumbnailUrl?: string;
  customThumbnailUrl?: string | null;  // null clears the saved value
  previewUrl?: string;
  connections?: ConnectionRequirement[];
  envVariables?: EnvVariable[];
  metadata?: AppMetadata;
}

export const appsApi = {
  /**
   * Get all active apps
   */
  async getAll(token: string, filters?: AppFilters): Promise<AppListItem[]> {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.tags) params.append('tags', filters.tags);
    if (filters?.search) params.append('search', filters.search);

    const queryString = params.toString();
    const url = queryString ? `/api/editor/apps?${queryString}` : '/api/editor/apps';

    const response = await apiClient.get(url, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get a single app by ID with full details
   */
  async getOne(token: string, appId: string): Promise<App> {
    const response = await apiClient.get(`/api/editor/apps/${appId}`, {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Get all categories with app counts
   */
  async getCategories(token: string): Promise<AppCategoryCount[]> {
    const response = await apiClient.get('/api/editor/apps/categories', {
      headers: createAuthHeaders(token),
    });
    return response.data;
  },

  /**
   * Validate connections before remix
   */
  async validateConnections(
    token: string,
    appId: string,
    dto: ValidateAppConnectionsDto,
  ): Promise<ValidateAppConnectionsResponse> {
    const response = await apiClient.post(
      `/api/editor/apps/${appId}/validate`,
      dto,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Remix an app to create a new project
   */
  async remix(
    token: string,
    appId: string,
    dto: RemixAppDto,
  ): Promise<RemixAppResponse> {
    const response = await apiClient.post(
      `/api/editor/apps/${appId}/remix`,
      dto,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  // =============================================================================
  // SUBMIT TO APP LIBRARY METHODS
  // =============================================================================

  /**
   * Get the app associated with a project (if already submitted)
   */
  async getProjectApp(token: string, projectId: string): Promise<App | null> {
    const response = await apiClient.get(
      `/api/editor/apps/project/${projectId}`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Get package.json info for auto-populating metadata/config in submit modal
   */
  async getProjectPackageInfo(token: string, projectId: string): Promise<{
    metadata: {
      version?: string;
      author?: string;
      framework?: string;
    };
    config: {
      buildCommand?: string;
      startCommand?: string;
      installCommand?: string;
    };
  }> {
    const response = await apiClient.get(
      `/api/editor/apps/project/${projectId}/package-info`,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Submit a project to the App Library
   */
  async submit(token: string, projectId: string, dto: SubmitAppDto): Promise<App> {
    const response = await apiClient.post(
      `/api/editor/apps/project/${projectId}/submit`,
      dto,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Re-submit an app with updated code and/or metadata
   */
  async resubmit(token: string, appId: string, dto: ResubmitAppDto): Promise<App> {
    const response = await apiClient.put(
      `/api/editor/apps/${appId}/resubmit`,
      dto,
      { headers: createAuthHeaders(token) },
    );
    return response.data;
  },

  /**
   * Remove an app from the library (soft delete)
   */
  async remove(token: string, appId: string): Promise<void> {
    await apiClient.delete(`/api/editor/apps/${appId}`, {
      headers: createAuthHeaders(token),
    });
  },
};
